import React, { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { FileText, Loader2, X } from 'lucide-react'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'

export default function DictamenPreview({ open, loading, text, verificationCode, modoDocumento = 'avanzado', rowsForAnnex = [], titularNombre = '', titularCuil = '', titularDni = '', tareasCuidadoResumen = null, includeAdvancedInterestColumn = false, hojaDeTrabajo = null, proyeccion = null, resumenEjecutivo = null, defensaJudicial = null, metodologiaSintetica = null, integridad = null, puntosRechazoPotenciales = [], anexosTecnicos = null, fundamentosPorLinea = [], consistenciaJuridica = null, preEmisionAudit = null, shadowBilling = null, scoreCertezaInforme = null, conciliacionManualActa = null, mutationsLog = [], onClose }) {
  const [visibleChars, setVisibleChars] = useState(0)
  const [appendixOptions, setAppendixOptions] = useState({
    resumen: true,
    metodologia: true,
    integridad: true,
    calculo: true,
  })

  const formatCurrency = (value) => {
    const n = Number(value || 0)
    return Number.isFinite(n)
      ? n.toLocaleString('es-AR', { maximumFractionDigits: 2 })
      : '0'
  }

  useEffect(() => {
    if (!open || loading || !text) return
    setVisibleChars(0)
    const id = setInterval(() => {
      setVisibleChars((v) => Math.min(v + 14, text.length))
    }, 16)
    return () => clearInterval(id)
  }, [open, loading, text])

  useEffect(() => {
    if (!open) return
    setAppendixOptions({
      resumen: anexosTecnicos?.includeResumenEjecutivo !== false,
      metodologia: anexosTecnicos?.includeMetodologia !== false,
      integridad: anexosTecnicos?.includeIntegridad !== false,
      calculo: anexosTecnicos?.includeCalculoPrestacional !== false,
    })
  }, [open, anexosTecnicos])

  const progressiveText = useMemo(() => text?.slice(0, visibleChars) || '', [text, visibleChars])
  const isBaseMode = modoDocumento === 'base'
  const documentLabel = isBaseMode ? 'CALCULADORA BASE (SIN FALLOS)' : 'MODO AVANZADO'

  const admisibilidad = useMemo(() => {
    const findings = []
    const warnings = []
    const score = Number(scoreCertezaInforme?.confianzaInforme ?? integridad?.scoreGlobal ?? 0)
    const annexRows = Array.isArray(rowsForAnnex) ? rowsForAnnex : []
    const simulationCase = annexRows.some((r) => Boolean(r?.__simulation))
    const totalRows = annexRows.length || 1
    const weakIndexRows = annexRows.filter((r) => {
      const baseMethod = String(r.indiceBaseMetodo || '')
      const ceseMethod = String(r.indiceCeseMetodo || '')
      return ['carry_forward', 'backfill', 'first_available', 'missing'].includes(baseMethod)
        || ['carry_forward', 'backfill', 'first_available', 'missing'].includes(ceseMethod)
    }).length
    const weakIndexPct = (weakIndexRows / totalRows) * 100

    if (!isBaseMode) {
      if (score < 85) {
        const msg = `Score de certeza insuficiente (${score.toFixed(2)} < 85).`
        if (simulationCase) warnings.push(msg)
        else findings.push(msg)
      }
      if (!shadowBilling?.enabled) {
        const msg = 'Shadow Billing no verificable por ausencia de totales de resumen en origen.'
        if (simulationCase) warnings.push(msg)
        else findings.push(msg)
      }
      if (weakIndexPct > 35) {
        const msg = `Calidad de índices insuficiente: ${weakIndexPct.toFixed(2)}% de filas con fallback fuerte.`
        if (simulationCase) warnings.push(msg)
        else findings.push(msg)
      }
      if (shadowBilling?.enabled && Array.isArray(conciliacionManualActa?.correcciones) && conciliacionManualActa.correcciones.length > 0 && !conciliacionManualActa?.hashIntegridad) {
        findings.push('Conciliación manual sin hash de integridad vinculante.')
      }
    }
    if (annexRows.some((r) => r.invalidIndex)) findings.push('Existen períodos sin índice oficial validado.')
    if (preEmisionAudit && preEmisionAudit.allowAdvancedDictamen === false && !isBaseMode) {
      findings.push(`Semáforo pre-emisión ${String(preEmisionAudit.color || 'rojo').toUpperCase()}: ${(preEmisionAudit.reasons || []).join(' | ')}`)
    }
    if (consistenciaJuridica && consistenciaJuridica.passed === false && !isBaseMode) {
      findings.push(`Consistencia jurídica pendiente: ${(consistenciaJuridica.issues || []).join(' | ')}`)
    }
    if (annexRows.some((r) => r.requiresValidation || r.requiereRevision)) {
      const msg = isBaseMode
        ? 'Hay filas con revisión pendiente. Puede emitir Informe Base, pero revise esas filas para mejorar precisión.'
        : 'Existen filas marcadas para revisión manual por legibilidad o consistencia.'
      if (isBaseMode) warnings.push(msg)
      else findings.push(msg)
    }
    return {
      aprobable: findings.length === 0,
      findings,
      warnings,
      score,
    }
  }, [isBaseMode, scoreCertezaInforme, integridad, shadowBilling, rowsForAnnex, conciliacionManualActa, preEmisionAudit, consistenciaJuridica])

  const cleanNoise = (value) => String(value || '')
    .replace(/Automa|matizado|Gal|Gaal/gi, '')
    .replace(/[^\x00-\x7F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  const safePeriodo = (raw, fallback = 'S/D') => {
    const txt = cleanNoise(raw)
    if (/^(0[1-9]|1[0-2])\/\d{4}$/.test(txt)) return txt
    const iso = txt.match(/^(\d{4})-(0[1-9]|1[0-2])$/)
    if (iso) return `${iso[2]}/${iso[1]}`
    return fallback
  }

  const stripMarkdown = (value) => String(value || '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1')

  const buildMutationsCanonicalJson = (input) => {
    const rows = (Array.isArray(input) ? input : []).map((m) => ({
      id: String(m?.id || ''),
      rowId: String(m?.rowId || ''),
      periodo: String(m?.periodo || ''),
      field: String(m?.field || ''),
      originalValue: m?.originalValue ?? null,
      newValue: m?.newValue ?? null,
      reasonCode: String(m?.reasonCode || ''),
      reasonDetail: String(m?.reasonDetail || ''),
      timestamp: String(m?.timestamp || ''),
      sourcePage: Number(m?.sourcePage || 0),
    }))
      .sort((a, b) => `${a.timestamp}|${a.id}`.localeCompare(`${b.timestamp}|${b.id}`))
    return JSON.stringify(rows)
  }

  const sha256Hex = async (raw) => {
    if (!globalThis?.crypto?.subtle) return 'hash_no_disponible'
    const bytes = new TextEncoder().encode(String(raw || ''))
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes)
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('')
  }

  if (!open) return null

  const onDownload = async () => {
    if (!text) return
    if (!admisibilidad.aprobable) return
    const doc = new jsPDF({ unit: 'pt', format: 'a4' })
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(14)
    doc.text(isBaseMode ? 'INFORME DE CÁLCULO PREVISIONAL BASE' : 'DICTAMEN PERICIAL PREVISIONAL', 40, 52)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    doc.text(`Generado: ${new Date().toLocaleString('es-AR')}`, 40, 70)
    doc.text(`Modo utilizado: ${documentLabel}`, 40, 84)
    doc.setFontSize(9)
    const actual = Number(resumenEjecutivo?.haberActualAnses || 0)
    const reaj = Number(resumenEjecutivo?.haberBolsilloReajustado || 0)
    const pct = Number(resumenEjecutivo?.incrementoMensualPct || 0)
    const dif = Number(resumenEjecutivo?.diferencialMensual || 0)
    const retro = Number(resumenEjecutivo?.retroactivoTotal || 0)
    doc.setFont('helvetica', 'bold')
    doc.text(isBaseMode ? 'RESUMEN DE CÁLCULO BASE' : 'RESUMEN PARA EL CLIENTE', 40, 100)
    doc.setFont('helvetica', 'normal')
    doc.rect(40, 108, 515, 84)
    doc.text(`Situación Actual (ANSES): $ ${formatCurrency(actual)}`, 52, 130)
    doc.text(`${isBaseMode ? 'Situación Estimada (Base)' : 'Situación Reajustada (Pericia)'}: $ ${formatCurrency(reaj)}`, 52, 148)
    doc.text(`Incremento Mensual: % ${pct.toFixed(2)}`, 52, 166)
    const frase = isBaseMode
      ? `Estimación base sin aplicación de fallos judiciales: diferencia mensual estimada de $${formatCurrency(dif)} y retroactivo técnico de $${formatCurrency(retro)}.`
      : `Bajo esta propuesta pericial, el titular percibirá un incremento inmediato de $${formatCurrency(dif)} en su haber mensual, más un pago único por retroactividad de $${formatCurrency(retro)}.`
    const fraseLines = doc.splitTextToSize(frase, 500)
    doc.text(fraseLines, 40, 208)

    const cleanDictamenText = stripMarkdown(text)
    const lines = doc.splitTextToSize(cleanDictamenText, 510)
    let y = 246
    lines.forEach((line) => {
      if (y > 790) {
        doc.addPage()
        y = 50
      }
      doc.text(line, 40, y)
      y += 14
    })

    if (proyeccion?.activa && proyeccion?.disclaimer) {
      if (y > 740) {
        doc.addPage()
        y = 56
      }
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(10)
      doc.setTextColor(130, 30, 30)
      const warning = doc.splitTextToSize(proyeccion.disclaimer, 510)
      doc.text(warning, 40, y + 16)
      y += warning.length * 12 + 8
    }

    if (y > 740) {
      doc.addPage()
      y = 56
    }
    doc.setFont('helvetica', 'italic')
    doc.setFontSize(10)
    doc.setTextColor(70)
    doc.text('Los datos volcados en el presente han sido extraidos del expediente administrativo de ANSES proporcionado por la parte.', 40, y + 16)
    doc.text('Espacio para firma profesional: ________________________________', 40, y + 34)

    doc.addPage()
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(18)
    const titularHeader = `DATOS DEL TITULAR: ${titularNombre || 'N/A'} - CUIL/CUIT: ${titularCuil || 'N/A'}`
    const titularLines = doc.splitTextToSize(titularHeader, 500)
    doc.text(titularLines, 40, 38)
    doc.setFontSize(13)
    doc.text(`ANEXO I: ${isBaseMode ? 'PLANILLA DE CALCULO BASE' : 'PLANILLA DE CALCULO ANALITICA'} - EXPTE: ${titularNombre || 'N/A'}`, 40, 52)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.text('Historia laboral completa + identificacion de Base de Calculo del Promedio (ultimas 120).', 40, 70)

    const allRowsSorted = (Array.isArray(rowsForAnnex) ? rowsForAnnex : [])
      .slice()
      .sort((a, b) => String(a.periodoKey || a.periodo || a.periodoISO || '').localeCompare(String(b.periodoKey || b.periodo || b.periodoISO || '')))

    const basePromedioRows = allRowsSorted
      .filter((r) => r.incluirEnPromedio !== false)
      .slice()
      .sort((a, b) => String(a.periodoKey || a.periodo || a.periodoISO || '').localeCompare(String(b.periodoKey || b.periodo || b.periodoISO || '')))
      .slice(-120)
    const baseSet = new Set(basePromedioRows.map((r) => String(r.periodoKey || r.periodo || r.periodoISO || '')))
    const antecedentesRows = allRowsSorted.filter((r) => !baseSet.has(String(r.periodoKey || r.periodo || r.periodoISO || '')))
    const periodoDesde = basePromedioRows[0]?.periodoKey || basePromedioRows[0]?.periodo || basePromedioRows[0]?.periodoISO || 'N/A'
    const periodoHasta = basePromedioRows[basePromedioRows.length - 1]?.periodoKey || basePromedioRows[basePromedioRows.length - 1]?.periodo || basePromedioRows[basePromedioRows.length - 1]?.periodoISO || 'N/A'
    const estimatedRows = allRowsSorted.filter((r) => Boolean(r.isEstimatedIndex))
    const officialRows = allRowsSorted.filter((r) => !r.isEstimatedIndex)
    const estimatedKeys = estimatedRows
      .map((r) => String(r.periodoKey || r.periodoISO || r.periodo || ''))
      .filter(Boolean)
      .sort()
    const estimatedFrom = estimatedKeys[0] || 'N/A'
    const estimatedTo = estimatedKeys[estimatedKeys.length - 1] || 'N/A'
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    doc.setTextColor(20)
    doc.text(`BASE DE CALCULO DEL PROMEDIO (120): desde ${periodoDesde} hasta ${periodoHasta}`, 40, 84)

    const boxTop = 94
    const hijos = Number(tareasCuidadoResumen?.hijos || 0)
    const hijosAnios = Number(tareasCuidadoResumen?.hijosAnios || 0)
    const discCant = Number(tareasCuidadoResumen?.discapacidadAuHCantidad || 0)
    const discAnios = Number(tareasCuidadoResumen?.discapacidadAuHAnios || 0)
    const totalAnios = Number(tareasCuidadoResumen?.totalAnios || 0)
    const showCuidado = hijos > 0 || discCant > 0 || totalAnios > 0
    if (showCuidado) {
      doc.setDrawColor(90)
      doc.setLineWidth(0.8)
      doc.rect(40, boxTop, 515, 68)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(10)
      doc.text('RECONOCIMIENTO DE SERVICIOS POR TAREAS DE CUIDADO', 48, boxTop + 14)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)
      doc.text(`Hijos: ${hijos} -> ${hijosAnios.toFixed(2)} años`, 48, boxTop + 30)
      doc.text(`Discapacidad/AUH: ${discCant} -> ${discAnios.toFixed(2)} años extra`, 48, boxTop + 45)
      doc.text(`Total reconocido por Ley 27.705: ${totalAnios.toFixed(2)} años`, 48, boxTop + 60)
    }

    const mapRow = (r) => {
      const periodo = safePeriodo(r.periodo || r.periodoISO || r.periodoKey || 'S/D')
      const periodoLabel = r.projected ? `${periodo} (PROYECTADO)` : periodo
      const empresa = cleanNoise(r.empresa || r.razonSocial || 'S/D') || 'S/D'
      const original = Number(r.montoOriginal || r.monto || r.remImpSS || 0)
      const legal = Number(r.montoSujetoAAporte || 0)
      const coef = Number(r.coeficiente || 1)
      const actualizado = Number(r.montoActualizado || legal)
      const monedaOrig = r.moneda ? String(r.moneda) : 'peso'
      const interesDev = Number((r.acumuladoConInteres || 0) - (r.diferenciaMensual || 0))
      const revisarMonto = Boolean(r._revisarMonto)
      const estimatedMark = r.isEstimatedIndex ? '*' : ''
      return [
        `${revisarMonto ? `${periodoLabel} (Revisar Monto)` : periodoLabel}${estimatedMark}`,
        empresa,
        formatCurrency(original),
        formatCurrency(legal),
        coef.toFixed(6),
        formatCurrency(actualizado),
        monedaOrig,
        formatCurrency(interesDev),
        includeAdvancedInterestColumn ? Number(r.factorInteres || 1).toFixed(8) : null,
        r.incluirEnPromedio === false ? 'No' : 'Si',
      ].filter((v) => v !== null)
    }

    for (let i = 1; i < basePromedioRows.length; i += 1) {
      const prev = Number(basePromedioRows[i - 1]?.montoOriginal || 0)
      const curr = Number(basePromedioRows[i]?.montoOriginal || 0)
      const prevTope = String(basePromedioRows[i - 1]?.fuenteNormativa || '') === 'tope_base_imponible'
      const currTope = String(basePromedioRows[i]?.fuenteNormativa || '') === 'tope_base_imponible'
      if (prev > 0 && !prevTope && !currTope) {
        const jump = ((curr - prev) / prev) * 100
        if (jump > 20) basePromedioRows[i]._revisarMonto = true
      }
    }

    const annexRows = basePromedioRows.slice().sort((a, b) => String(a.periodoKey || a.periodo || a.periodoISO || '').localeCompare(String(b.periodoKey || b.periodo || b.periodoISO || ''))).map(mapRow)

    const bodyStyles = annexRows.map((r) => {
      const enBase = r[7] === 'Si'
      return {
        fillColor: enBase ? [214, 255, 230] : false,
        fontStyle: enBase ? 'bold' : 'normal',
      }
    })

    autoTable(doc, {
      startY: showCuidado ? 172 : 104,
      head: [[
        'Mes/Año',
        'Empresa',
        'Sueldo Original (PDF)',
        'Tope Imponible Aplicado',
        'Indice Coeficiente',
        'Sueldo Actualizado',
        'Moneda Orig.',
        'Intereses Devengados',
        ...(includeAdvancedInterestColumn ? ['Coef. Interes'] : []),
        'Base 120',
      ]],
      body: annexRows,
      styles: {
        fontSize: 8,
        cellPadding: 5,
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: [32, 42, 57],
      },
      alternateRowStyles: {
        fillColor: [243, 244, 246],
      },
      didParseCell: (hookData) => {
        if (hookData.section === 'body') {
          const st = bodyStyles[hookData.row.index]
          if (st?.fillColor) hookData.cell.styles.fillColor = st.fillColor
          if (st?.fontStyle) hookData.cell.styles.fontStyle = st.fontStyle
        }
      },
      margin: { left: 40, right: 40 },
    })

    const auditRows = Array.isArray(mutationsLog) ? mutationsLog : []
    if (auditRows.length > 0) {
      const canonical = buildMutationsCanonicalJson(auditRows)
      const integrityHash = await sha256Hex(canonical)
      doc.addPage()
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(12)
      doc.text('Anexo Tecnico: Matriz de Novedades y Ajustes Manuales de la Pericia', 40, 46)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)
      doc.text('Registro inmutable de cambios manuales aplicados sobre datos de origen.', 40, 62)
      autoTable(doc, {
        startY: 72,
        head: [['Fecha', 'Periodo', 'Campo', 'Cambio', 'Motivo', 'Justificacion']],
        body: auditRows.map((m) => [
          new Date(m.timestamp || Date.now()).toLocaleString('es-AR', { hour12: false }),
          String(m.periodo || '-'),
          String(m.field || '-'),
          `${String(m.originalValue ?? '')} -> ${String(m.newValue ?? '')}`,
          String(m.reasonCode || '-'),
          String(m.reasonDetail || '-'),
        ]),
        styles: { fontSize: 7, cellPadding: 3 },
        headStyles: { fillColor: [20, 31, 58] },
      })
      const tableEndY = doc.lastAutoTable?.finalY || 700
      doc.setFont('courier', 'normal')
      doc.setFontSize(8)
      doc.setTextColor(60)
      doc.text(`Hash Integridad Mutaciones (SHA-256): ${integrityHash}`, 40, Math.min(810, tableEndY + 18))
      doc.text(`Total mutaciones: ${auditRows.length} | UTC: ${new Date().toISOString()}`, 40, Math.min(822, tableEndY + 30))
    }

    if (antecedentesRows.length > 0) {
      doc.addPage()
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(12)
      doc.text('ANTECEDENTES LABORALES (FUERA DE BASE 120)', 40, 44)
      autoTable(doc, {
        startY: 56,
        head: [[
          'Mes/Año',
          'Empresa',
          'Sueldo Original (PDF)',
          'Tope Imponible Aplicado',
          'Indice Coeficiente',
          'Sueldo Actualizado',
          'Moneda Orig.',
        ]],
        body: antecedentesRows.map((r) => {
           const periodo = safePeriodo(r.periodo || r.periodoISO || r.periodoKey || 'S/D')
          const original = Number(r.montoOriginal || r.monto || r.remImpSS || 0)
          const legal = Number(r.montoSujetoAAporte || 0)
          const coef = Number(r.coeficiente || 1)
          const actualizado = Number(r.montoActualizado || legal)
          const monedaOrig = r.moneda ? String(r.moneda) : 'peso'
          const empresa = cleanNoise(r.empresa || r.razonSocial || 'S/D') || 'S/D'
          return [periodo, empresa, formatCurrency(original), formatCurrency(legal), coef.toFixed(6), formatCurrency(actualizado), monedaOrig]
        }),
        styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
        headStyles: { fillColor: [55, 65, 81] },
        alternateRowStyles: { fillColor: [243, 244, 246] },
        margin: { left: 40, right: 40 },
      })
    }

    if (hojaDeTrabajo && appendixOptions.calculo) {
      doc.addPage()
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(14)
      doc.setTextColor(20)
      doc.text('HOJA DE TRABAJO METODOLOGICA', 40, 52)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      const divisorLabel = Number(hojaDeTrabajo.divisor || 120)
      doc.text(`Suma de Remuneraciones: ${formatCurrency(hojaDeTrabajo.numerador)}`, 40, 78)
      doc.text(`Suma de Remuneraciones / ${divisorLabel} = Promedio`, 40, 94)
      doc.text(`Promedio resultante: ${formatCurrency(hojaDeTrabajo.promedioResultante)}`, 40, 110)
      const fundamentoLines = doc.splitTextToSize(`Justificacion del divisor: ${hojaDeTrabajo.fundamento}`, 510)
      doc.text(fundamentoLines, 40, 128)
      doc.text(`Rango base 120: ${safePeriodo(hojaDeTrabajo.periodoDesde, '-')} a ${safePeriodo(hojaDeTrabajo.periodoHasta, '-')}`, 40, 128 + fundamentoLines.length * 14)
    }

    if (!isBaseMode && appendixOptions.metodologia && metodologiaSintetica?.pasos?.length) {
      doc.addPage()
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(14)
      doc.text('ANEXO TECNICO - METODOLOGIA', 40, 52)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      let yMeta = 78
      metodologiaSintetica.pasos.forEach((paso, idx) => {
        const lines = doc.splitTextToSize(`${idx + 1}. ${paso}`, 510)
        if (yMeta > 780) {
          doc.addPage()
          yMeta = 52
        }
        doc.text(lines, 40, yMeta)
        yMeta += lines.length * 14 + 4
      })
    }

    if (appendixOptions.integridad && integridad) {
      doc.addPage()
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(14)
      doc.text('ANEXO TECNICO - INTEGRIDAD Y TRAZABILIDAD', 40, 52)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      doc.text(`Score global de identidad: ${Number(integridad.scoreGlobal || 0).toFixed(2)}%`, 40, 78)
      doc.text(`Filas FACTUAL: ${integridad.factual || 0}`, 40, 96)
      doc.text(`Filas INFERRED: ${integridad.inferred || 0}`, 40, 114)
      doc.text(`Filas CORRUPT: ${integridad.corrupt || 0}`, 40, 132)
      doc.text(`Total filas analizadas: ${integridad.totalFilas || 0}`, 40, 150)
    }

    if (!isBaseMode) {
      doc.addPage()
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(14)
      doc.text('ANEXO TÉCNICO - TRAZABILIDAD DE ÍNDICES', 40, 52)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      doc.text(`Filas con índice oficial: ${officialRows.length}`, 40, 78)
      doc.text(`Filas con índice estimado: ${estimatedRows.length}`, 40, 96)
      doc.text(`Rango de estimación técnica: ${estimatedFrom} a ${estimatedTo}`, 40, 114)
      doc.text('Criterio: cuando no existe publicación oficial mensual, se aplica estimación técnica con marcación explícita (*) y trazabilidad por período.', 40, 132, { maxWidth: 510 })
      doc.text('Validez forense: el resultado conserva auditabilidad fila a fila (período, base, coeficiente, fuente y marca de estimado).', 40, 164, { maxWidth: 510 })
    }

    if (!isBaseMode && Array.isArray(fundamentosPorLinea) && fundamentosPorLinea.length > 0) {
      doc.addPage()
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(14)
      doc.text('ANEXO TÉCNICO - FUNDAMENTO POR LÍNEA', 40, 52)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      doc.text('Justificación técnico-jurídica fila por fila (índice, interés, norma y etiquetas de riesgo).', 40, 70)
      autoTable(doc, {
        startY: 82,
        head: [['Periodo', 'Fundamento']],
        body: fundamentosPorLinea.map((f) => [String(f.periodo || 'N/D'), String(f.rationale || '')]),
        styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
        headStyles: { fillColor: [55, 65, 81] },
        alternateRowStyles: { fillColor: [243, 244, 246] },
        margin: { left: 40, right: 40 },
      })
    }

    if (!isBaseMode && appendixOptions.resumen && defensaJudicial) {
      doc.addPage()
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(14)
      doc.text('ANEXO TÉCNICO - DEFENSA DEL CÁLCULO', 40, 52)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      const tesisLines = doc.splitTextToSize(`Tesis: ${defensaJudicial.tesis || ''}`, 510)
      doc.text(tesisLines, 40, 78)
      let yDef = 98 + tesisLines.length * 12
      ;(defensaJudicial.puntosClave || []).forEach((p, idx) => {
        const lines = doc.splitTextToSize(`${idx + 1}. ${p}`, 510)
        if (yDef > 780) {
          doc.addPage()
          yDef = 52
        }
        doc.text(lines, 40, yDef)
        yDef += lines.length * 14 + 4
      })
    }

    if (!isBaseMode) {
      doc.addPage()
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(14)
      doc.text('ANEXO TÉCNICO - CONCILIACIÓN SHADOW BILLING', 40, 52)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      doc.text(`Estado: ${shadowBilling?.enabled ? 'Verificable' : 'No verificable'}`, 40, 74)
      doc.text(`Umbral de desbalance: ${Number(shadowBilling?.thresholdPct || 0).toFixed(3)}%`, 40, 90)
      if (conciliacionManualActa?.hashIntegridad) {
        doc.text(`Hash de Integridad (acta manual): ${conciliacionManualActa.hashIntegridad}`, 40, 106)
      }
      if (!shadowBilling?.enabled) {
        doc.setTextColor(140, 35, 35)
        doc.text('Conciliación de integridad no verificada por falta de datos de origen.', 40, 108)
        doc.setTextColor(20)
      } else {
        autoTable(doc, {
          startY: conciliacionManualActa?.hashIntegridad ? 122 : 106,
          head: [['CUIT', 'Total Resumen', 'Total Detalle', 'Diferencia', 'Diferencia %', 'Urgente']],
          body: (shadowBilling?.desbalancePorCuit || []).map((r) => [
            String(r.cuit || 'S/D'),
            formatCurrency(r.totalResumen || 0),
            formatCurrency(r.totalDetalle || 0),
            formatCurrency(r.diferencia || 0),
            Number(r.diferenciaPct || 0).toFixed(3),
            r.requiereRevisionUrgente ? 'SI' : 'NO',
          ]),
          styles: { fontSize: 8, cellPadding: 4 },
          headStyles: { fillColor: [55, 65, 81] },
          margin: { left: 40, right: 40 },
        })
      }
    }

    if (!isBaseMode) {
      doc.addPage()
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(14)
      doc.text('ANEXO TÉCNICO - PUNTOS DE RECHAZO POTENCIALES', 40, 52)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      const mergedFindings = [
        ...(Array.isArray(puntosRechazoPotenciales) ? puntosRechazoPotenciales : []),
        ...admisibilidad.findings,
      ]
      if (!mergedFindings.length) {
        doc.text('No se detectaron puntos de rechazo automáticos con los guardrails vigentes.', 40, 74)
      } else {
        let yRisk = 76
        mergedFindings.forEach((item, idx) => {
          const lines = doc.splitTextToSize(`${idx + 1}. ${item}`, 510)
          if (yRisk > 780) {
            doc.addPage()
            yRisk = 52
          }
          doc.text(lines, 40, yRisk)
          yRisk += lines.length * 14 + 4
        })
      }
    }

    const finalY = doc.lastAutoTable?.finalY || 760
    const fuenteInteres = rowsForAnnex?.[0]?.tasaInteresFuente || 'BCRA - Serie Histórica Com. 14.290'
    const fechaCorte = rowsForAnnex?.[0]?.tasaInteresFechaCorte || new Date().toLocaleDateString('es-AR')
    const footerShift = conciliacionManualActa?.hashIntegridad ? 12 : 0
    doc.setFont('helvetica', 'italic')
    doc.setFontSize(9)
    doc.setTextColor(70)
    doc.text(`Documento generado mediante Sistema de Auditoría Previsional v4.0 - Verificación de Integridad: ${verificationCode || 'N/A'}`, 40, Math.min(810, finalY + 18))
    if (conciliacionManualActa?.hashIntegridad) {
      doc.text(`Acta de conciliación manual hash: ${conciliacionManualActa.hashIntegridad}`, 40, Math.min(822, finalY + 30))
    }
    doc.text(`Fuente: ${fuenteInteres} | Fecha de Corte de la Tasa: ${fechaCorte}`, 40, Math.min(822 + footerShift, finalY + 30 + footerShift))
    doc.text('Los coeficientes de interés aplicados corresponden a la serie oficial validada a la fecha de emisión de este informe.', 40, Math.min(834 + footerShift, finalY + 42 + footerShift))
    doc.text('Método de interés: capitalización compuesta sobre base mensual acumulada.', 40, Math.min(846 + footerShift, finalY + 54 + footerShift))
    doc.text('Nota: Coeficientes de actualización = índice oficial al cese / índice oficial del mes de remuneración (ISBIC/RIPTE).', 40, Math.min(858 + footerShift, finalY + 66 + footerShift))
    doc.text('*Valor estimado técnicamente por falta de publicación oficial a la fecha de corte.', 40, Math.min(870 + footerShift, finalY + 78 + footerShift))
    doc.text('Filas etiquetadas como (PROYECTADO): Monto estimado en base al último salario real actualizado por índice de movilidad oficial.', 40, Math.min(882 + footerShift, finalY + 90 + footerShift))
    doc.text('Tramo final calculado con índices estimados por falta de publicación oficial a la fecha de corte.', 40, Math.min(894 + footerShift, finalY + 102 + footerShift))

    const pages = doc.getNumberOfPages()
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i)
      doc.setDrawColor(170)
      doc.setLineWidth(0.6)
      doc.rect(16, 16, 563, 810)
      doc.setFont('courier', 'bold')
      doc.setFontSize(9)
      doc.setTextColor(95)
      doc.text(`VerificationCode: ${verificationCode || 'N/A'}`, 574, 120, { align: 'right', angle: 90 })
      doc.setFontSize(9)
      doc.setTextColor(90)
      doc.text(`Código de Verificación: ${verificationCode || 'N/A'} | Modo: ${documentLabel}`, 40, 820)
      doc.text(`Página ${i}/${pages}`, 540, 820, { align: 'right' })
    }

    doc.save(isBaseMode ? 'informe-calculo-base.pdf' : 'legajo-judicial-completo.pdf')
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="panel-profile w-full max-w-5xl rounded-3xl p-5 shadow-[0_30px_80px_rgba(0,0,0,0.35)] backdrop-blur-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <div className="inline-flex items-center gap-2 text-slate-100">
            <FileText className="h-5 w-5 text-profile-accent" />
            <h3 className="text-lg font-semibold">{isBaseMode ? 'Informe de Cálculo Base' : 'Dictamen Forense (Gemini)'}</h3>
          </div>
          <button type="button" onClick={onClose} className="panel-profile rounded-xl p-2 text-slate-200 hover:bg-white/20">
            <X className="h-4 w-4" />
          </button>
        </div>

        {loading ? (
          <div className="panel-profile rounded-2xl p-5">
            <p className="mb-3 text-sm text-profile-accent">Gemini analizando confiscatoriedad...</p>
            <div className="space-y-2">
              <div className="h-4 w-full animate-pulse rounded bg-white/20" />
              <div className="h-4 w-11/12 animate-pulse rounded bg-white/15" />
              <div className="h-4 w-10/12 animate-pulse rounded bg-white/15" />
            </div>
            <div className="mt-4 inline-flex items-center gap-2 text-xs text-slate-300"><Loader2 className="h-4 w-4 animate-spin" /> Escaneo de jurisprudencia en curso</div>
          </div>
        ) : (
          <>
            <motion.pre
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="panel-profile max-h-[62vh] overflow-auto whitespace-pre-wrap rounded-2xl p-4 text-sm leading-6 text-slate-100"
            >
               {isBaseMode
                 ? 'Informe base: estimacion previsional sin aplicacion de fallos judiciales.\n\n'
                 : 'Metodologia: El promedio de remuneraciones para la determinacion de la PC y PAP se calcula sobre los ultimos 120 meses con aportes computables (Art. 24, Ley 24.241).\n\n'}
                {stripMarkdown(progressiveText)}
              </motion.pre>
            <div className="mt-4 flex justify-end">
              <div className="mr-auto flex flex-wrap items-center gap-2 text-xs text-slate-200">
                <span className="panel-profile-accent rounded px-2 py-1 text-profile-accent">Modo: {documentLabel}</span>
                {!isBaseMode && <label className="inline-flex items-center gap-1"><input type="checkbox" checked={appendixOptions.resumen} onChange={(e) => setAppendixOptions((p) => ({ ...p, resumen: e.target.checked }))} /> Defensa</label>}
                {!isBaseMode && <label className="inline-flex items-center gap-1"><input type="checkbox" checked={appendixOptions.metodologia} onChange={(e) => setAppendixOptions((p) => ({ ...p, metodologia: e.target.checked }))} /> Metodologia</label>}
                <label className="inline-flex items-center gap-1"><input type="checkbox" checked={appendixOptions.integridad} onChange={(e) => setAppendixOptions((p) => ({ ...p, integridad: e.target.checked }))} /> Integridad</label>
                <label className="inline-flex items-center gap-1"><input type="checkbox" checked={appendixOptions.calculo} onChange={(e) => setAppendixOptions((p) => ({ ...p, calculo: e.target.checked }))} /> Hoja Trabajo</label>
              </div>
              {!admisibilidad.aprobable && (
                <div className="panel-profile-danger mr-3 max-w-md rounded-lg px-3 py-2 text-xs text-profile-danger">
                  Exportacion bloqueada por guardrails: {admisibilidad.findings.join(' | ')}
                </div>
              )}
              {admisibilidad.aprobable && Array.isArray(admisibilidad.warnings) && admisibilidad.warnings.length > 0 && (
                <div className="panel-profile-warn mr-3 max-w-md rounded-lg px-3 py-2 text-xs text-profile-warn">
                  Aviso: {admisibilidad.warnings.join(' | ')}
                </div>
              )}
              {conciliacionManualActa?.hashIntegridad && (
                <div className="panel-profile-accent mr-3 max-w-md rounded-lg px-3 py-2 text-xs text-profile-accent">
                  Hash de Integridad de correcciones manuales: {conciliacionManualActa.hashIntegridad}
                </div>
              )}
              <button type="button" onClick={onDownload} className="btn-profile-accent rounded-xl px-5 py-2.5 text-sm font-semibold shadow-[0_12px_30px_rgba(56,189,248,0.35)] hover:brightness-110">
                {isBaseMode ? 'Descargar Informe de Calculo Base (PDF)' : 'Descargar Legajo Judicial Completo (PDF)'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
